import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../Images/Logo.svg";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("home");

  const navigate = useNavigate();
  const location = useLocation();

  // 🔹 MENU → SECTION MAPPING
  const sectionMap = {
    about: "about",
    courses: "courses",
    blog: "testimonials",
    contact: "contact",
  };

  // 🔹 Sync active link with URL
  useEffect(() => {
    const path = location.pathname.replace("/", "") || "home";
    setActiveLink(path);
  }, [location.pathname]);

  // 🔹 Scroll after navigating to home
  useEffect(() => {
    if (location.state?.scrollTo) {
      const section = document.getElementById(location.state.scrollTo);
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLinkClick = (link) => {
    setActiveLink(link);
    setIsMenuOpen(false);

    // HOME
    if (link === "home") {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const targetSection = sectionMap[link];

    // If already on home page → scroll
    if (location.pathname === "/") {
      const section = document.getElementById(targetSection);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }

    // From other pages → go home & scroll
    navigate("/", {
      state: { scrollTo: targetSection },
    });
  };

  const links = ["home", "about", "courses", "blog", "contact"];

  return (
    <header className="w-full bg-white shadow-md z-50 sticky top-0">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[80px]">

          {/* Logo */}
          <div
            className="cursor-pointer"
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img src={Logo} alt="SkillMD" className="w-32" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex gap-6">
            {links.map((link) => (
              <button
                key={link}
                onClick={() => handleLinkClick(link)}
                className={`relative font-medium transition ${
                  activeLink === link
                    ? "text-orange-500 font-semibold"
                    : "text-gray-800 hover:text-orange-500"
                }`}
              >
                {link === "courses"
                  ? "Our Courses"
                  : link.charAt(0).toUpperCase() + link.slice(1)}
                {activeLink === link && (
                  <span className="absolute left-0 -bottom-2 w-full h-[3px] bg-orange-500 rounded"></span>
                )}
              </button>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="bg-cyan-500 text-white px-6 py-2 rounded hover:bg-cyan-600"
            >
              Login
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 border rounded"
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t py-4">
            <nav className="flex flex-col gap-2">
              {links.map((link) => (
                <button
                  key={link}
                  onClick={() => handleLinkClick(link)}
                  className={`px-4 py-2 text-left rounded ${
                    activeLink === link
                      ? "bg-orange-50 text-orange-500 font-semibold"
                      : "text-gray-800"
                  }`}
                >
                  {link === "courses"
                    ? "Our Courses"
                    : link.charAt(0).toUpperCase() + link.slice(1)}
                </button>
              ))}
              <button
                onClick={() => navigate("/login")}
                className="mt-2 bg-cyan-500 text-white py-2 rounded"
              >
                Login
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
